import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { User } from '../interfaces/user';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * @author Mariam
 * @dateCreated 5 Aug 2023
 * @description responsible for handling HTTP requests related to users.
 *              It provides methods for retrieving, creating, and deleting user accounts using the API endpoint
 *              specified in the 'environment' constant.
 * @modifiedBy Mariam
 * @dateModified 9 Aug 2023
 */
@Injectable({
  providedIn: 'root'
})
export class UsersService {
  baseUrl: string = environment.API_SERVER;

  constructor(private _http: HttpClient) { }


  /**
 * @author Mariam
 * @dateCreated 28 Aug 2023
 * @param {any[]} array - An array of objects containing role information.
 * @returns {string[]} An array of role names extracted from the input array.
 * @description Extracts role names from an array of objects.
 * @modifiedBy
 * @dateModified
 */
  getRoleNames(array: any) {

    let names: string[] = [];
    let namesAndIdsArr: string[][] = [];
    for (let obj of array) {
      if (obj.roleName) {
        names.push(obj.roleName);
      }
      if (obj.id) {
        namesAndIdsArr.push([obj.id, obj.roleName]);
      }

      localStorage.setItem('rolesList', JSON.stringify(namesAndIdsArr))

    }
    return names;
  }
  /**
 * @method getRoleNameById
 * @author Mariam
 * @dateCreated 8 Aug 2023
 * @description Retrieves the role name based on the provided role ID.
 * @param {string} roleId - The ID of the role.
 * @returns {string} The name of the role.
 * @modifiedBy None
 * @dateModified None
 */
  getRoleNameById(roleId) {
    // assign role id to the role
    let roleName = ''
    // Iterate through the rolesList array in localStorage to find the matching role ID
    for (let role of JSON.parse(localStorage.getItem('rolesList'))) {
      if (role[0] === roleId) {
        roleName = role[1];
      }
    }
    return roleName;
  }


  /**
  * @method getRoleId
  * @author Mariam
  * @dateCreated 21 Aug 2023
  * @description Retrieves the role ID based on the provided role name.
  * @param {string} roleName
  * @returns {string} the role ID
  * @modifiedBy None
  * @dateModified None
  * @reasonForModification None
  */
  getRoleId(roleName) {
    // assign role id to the role
    let roleId = ''
    // Iterate through the rolesList array to find the matching organization Name
    for (let role of JSON.parse(localStorage.getItem('rolesList'))) {
      if (role[1] === roleName) {
        roleId = role[0];
      }
    }
    return roleId;

  }

  /**
 * @method getOrganizationNames
 * @param {any[]} array - An array of objects containing organization information.
 * @returns {string[]} An array of organization names extracted from the input array.
 * @author Mariam
 * @dateCreated 28 Aug 2023
 * @description Extracts organization names from an array of objects.
 * @modifiedBy
 * @dateModified
 */
  getOrganizationNames(array: any) {

    let names: string[] = [];
    let namesAndIdsArr: string[][] = [];
    for (let o of array) {
      if (o.organizationName) {
        names.push(o.organizationName);
      }
      if (o.id) {
        namesAndIdsArr.push([o.id, o.organizationName]);
      }

      localStorage.setItem('id', JSON.stringify(namesAndIdsArr))

    }
    return names;
  }


  /**
   * @author Mariam
   * @dateCreated 28 Aug 2023
   * @param {string} organization - The ID of the organization for which the name needs to be retrieved.
   * @returns {string | undefined} - The name of the organization corresponding to the provided ID, or undefined if not found.
   * @description Retrieves the name of the organization based on the provided organization ID.
   * @modifiedBy -
   * @dateModified -
   */
  getOrganizationNameById(organization) {
    let orgName;
    // Iterate through the organizations array in localStorage to find the matching organization ID
    for (let org of JSON.parse(localStorage.getItem('id'))) {
      if (org[0] === organization) {
        orgName = org[1];
        // this.organizationName = org[1];
      }
    }

    return orgName;
  }

  /**
* @author Mariam
* @dateCreated 28 Aug 2023
* @param {string} organization - The Name of the organization for which the id needs to be retrieved.
* @returns {string | undefined} - The ID of the organization corresponding to the provided Name, or undefined if not found.
* @description Retrieves the ID of the organization based on the provided organization name.
* @modifiedBy -
* @dateModified -
*/
  getOrganizationIdByName(orgName) {
    let orgId;
    // Iterate through the organizations array in LocalStorage to find the matching organization Name
    for (let org of JSON.parse(localStorage.getItem('id'))) {
      if (org[1] === orgName) {
        orgId = org[0];
      }
    }

    return orgId;
  }

  getUsers(payload: { params: any }): Observable<any> {
    return this._http.get(`${this.baseUrl}users`, { params: payload?.params })
  }

  getUserData(payload: { params: any }): Observable<any> {
    return this._http.get(this.baseUrl + 'user/me', { params: payload?.params });
  }

  getCurrentUser(): any {
    let userData: string = localStorage.getItem('userData');
    return JSON.parse(userData);
  }

  changePassword(payload: any): Observable<any> {
    return this._http.patch(
      `
      ${this.baseUrl}
      // users/me/update-password
      `,
      payload.body)
  }

  createnewUser(payload: any): Observable<any> {

    return this._http.post(`${this.baseUrl}users`,
      payload.body);
  }

  updateUser(payload: any): Observable<any> {
    return this._http.patch(`${this.baseUrl}users/${payload?.userId}`,
      payload.body);
  }

  updateUserRole(payload: { userId: string, roleId: string }): Observable<any> {
    return this._http.patch(`${this.baseUrl}users/${payload?.userId}/role/${payload.roleId}`, payload)
  }

  updateUserStatus(payload: any): Observable<any> {
    return this._http.patch(`${this.baseUrl}users/${payload?.userId}/status/`, payload.body)
  }


  resetUserPassword(payload: any): Observable<any> {
    return this._http.patch(`${this.baseUrl}users/${payload?.userId}/reset-user-password`, payload.body)
  }

  deleteUser(payload: { userId: string }): Observable<any> {
    return this._http.delete(`${this.baseUrl}users/${payload?.userId}`)

  }



}


